/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, Stack, Alert, Chip,
    CircularProgress, Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Iconify from '../Iconify';
import { formatSlotTime, calcEndTime, formatLocalDate } from '../../utils/pmsTimeUtils';
import { callApi } from '../../utils/apiCall';
import { loadScheduleAvailability, slotIdentity, uniqueSlots } from './appointments/nextAvailable';
import useSnackbar from '../../hooks/useCustomSnackbar';
import { usePopupFailure } from '../../contexts/PopupFailureContext';
import { sectionCardStyle, sectionLabelStyle, slotCardStyle, slotTimeStyle } from '../../styles/DentallyBookingStyle';

const MORNING_CUTOFF_HOUR = 12;
const AFTERNOON_CUTOFF_HOUR = 17;

function groupSlotsByPeriod(slots, timeZone) {
    const morning = [];
    const afternoon = [];
    const evening = [];
    slots.forEach((slot) => {
        const zone = slot._timezone || timeZone;
        const hour = zone ? Number(new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: 'numeric', hourCycle: 'h23' }).format(new Date(slot.start_time))) : new Date(slot.start_time).getHours();
        if (hour < MORNING_CUTOFF_HOUR) morning.push(slot);
        else if (hour < AFTERNOON_CUTOFF_HOUR) afternoon.push(slot);
        else evening.push(slot);
    });
    return { morning, afternoon, evening };
}


function SlotPeriodSection({ label, icon, iconColor, slots, selectedSlot, onSelect, duration, timeZone }) {
    if (!slots.length) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75, px: 0.5 }}>
                <Iconify icon={icon} width={14} sx={{ color: iconColor }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                    {label}
                </Typography>
                <Chip
                    label={slots.length}
                    size="small"
                    sx={{ fontSize: '10px', height: 18, fontWeight: 600, bgcolor: 'rgba(145,158,171,0.08)', color: 'text.disabled' }}
                />
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 0.75 }}>
                {slots.map((slot) => {
                    const isSelected = selectedSlot?.start_time === slot.start_time && selectedSlot?.practitioner_id === slot.practitioner_id && selectedSlot?._schedule_id === slot._schedule_id;
                    return (
                        <Box
                            component="button"
                            type="button"
                            key={`${slot.start_time}-${slot.practitioner_id}-${slot._schedule_id}`}
                            onClick={() => onSelect(slot)}
                            sx={{
                                ...slotCardStyle(isSelected, iconColor),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                minHeight: 40,
                            }}
                        >
                            <Typography sx={slotTimeStyle}>
                                {formatSlotTime(slot.start_time, slot._timezone || timeZone)} &ndash; {(slot.end_time ? formatSlotTime(slot.end_time, slot._timezone || timeZone) : calcEndTime(slot.start_time, duration, slot._timezone || timeZone))}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

/**
 * PMS Availability Wizard Step — embedded within the standard CRM follow-up flow.
 *
 * Active when pmsEnabledGlobal=true AND dentallyEnabledGlobal=false.
 *
 * Mirrors the visual style of DentallyTreatmentAvailabilityWizardStep.
 *
 * Props (controlled from parent):
 *   practiceId          — required
 *   pmsSlot             — currently selected slot object (or null)
 *   onSlotSelected      — (slot | null) => void
 *   selectedPractitioner — practitioner object (or null)
 *   onPractitionerSelected — (practitioner | null) => void
 *   selectedDate        — Date object (or null)
 *   onDateSelected      — (Date | null) => void
 *   appointmentDuration — integer minutes (default 30)
 *   handleNext          — called when wizard "Next" fires
 */
const PmsAvailabilityWizardStep = forwardRef(({
    handleNext: onSubmit,
    timeZone,
    practiceId,
    leadId,
    treatmentId,
    requireTreatment = false,
    pmsSlot,
    suggestedSlot,
    onSlotSelected,
    selectedPractitioner,
    onPractitionerSelected,
    selectedDate,
    onDateSelected,
    appointmentDuration = 30,
    onDurationChange,
}, ref) => {
    const { enqueueSnackbar } = useSnackbar();

    const suggestionKey = slotIdentity(suggestedSlot);
    const [suggestionMissing, setSuggestionMissing] = useState(false);
    const [duration, setDuration] = useState(appointmentDuration);

    const [practitioners, setPractitioners] = useState([]);
    const [practitionersLoading, setPractitionersLoading] = useState(false);
    const [practitionersError, setPractitionersError] = useState(null);

    const [metadataRetry, setMetadataRetry] = useState(0);
    const [schedulesError, setSchedulesError] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);

    const slotsRequest = useRef(0);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsError, setSlotsError] = useState(null);

    // Report load failures to the popup (once per new error value) so the lead
    // can be pinned with them.
    const { report: reportFailure } = usePopupFailure();
    useEffect(() => { if (practitionersError) reportFailure(practitionersError, 'Availability'); }, [practitionersError]);
    useEffect(() => { if (schedulesError) reportFailure(schedulesError, 'Availability'); }, [schedulesError]);
    useEffect(() => { if (slotsError) reportFailure(slotsError, 'Availability'); }, [slotsError]);

    // Sync duration when the prop changes
    useEffect(() => {
        setDuration(appointmentDuration);
    }, [appointmentDuration]);

    // Load practitioners on mount
    useEffect(() => {
        if (!practiceId) return undefined;
        let cancelled = false;
        setPractitioners([]);
        setPractitionersLoading(true);
        setPractitionersError(null);

        callApi(`/api/v1/practices/${practiceId}/pms/practitioners/`)
            .then((r) => {
                if (cancelled) return;
                const results = r?.data?.results ?? r?.data;
                if (!r?.ok || !Array.isArray(results)) throw new Error();
                setPractitioners(Array.isArray(results) ? results : []);
            })
            .catch(() => {
                if (!cancelled) setPractitionersError('Failed to load practitioners. Please try again.');
            })
            .finally(() => {
                if (!cancelled) setPractitionersLoading(false);
            });

        return () => { cancelled = true; };
    }, [practiceId, metadataRetry]);

    // Load schedules when practitioner changes
    useEffect(() => {
        if (!practiceId || !selectedPractitioner?.id) {
            setSchedules([]);
            return undefined;
        }
        let cancelled = false;
        setSchedules([]);
        setSchedulesError(null);
        setSchedulesLoading(true);

        callApi(`/api/v1/practices/${practiceId}/pms/practitioners/${selectedPractitioner.id}/schedules/`)
            .then((r) => {
                if (cancelled) return;
                const results = r?.data?.results ?? r?.data;
                if (!r?.ok || !Array.isArray(results)) throw new Error();
                setSchedules(Array.isArray(results) ? results : []);
            })
            .catch(() => { if (!cancelled) { setSchedules([]); setSchedulesError('Failed to load schedules. Please try again.'); } })
            .finally(() => { if (!cancelled) setSchedulesLoading(false); });

        return () => { cancelled = true; };
    }, [practiceId, selectedPractitioner?.id, metadataRetry]);

    // Load slots when practitioner + date + schedule are available
    const fetchSlots = useCallback(async () => {
        slotsRequest.current += 1;
        const request = slotsRequest.current;
        setSlots([]);
        setSuggestionMissing(false);
        if (!practiceId || !selectedPractitioner?.id || !selectedDate || (requireTreatment && !treatmentId)) return;
        if (!schedules.length) return;

        setSlotsLoading(true);
        setSlotsError(null);
        setSlots([]);

        if (Number.isNaN(selectedDate.getTime())) { setSlotsLoading(false); return; }
        const dateStr = formatLocalDate(selectedDate);

        try {
            const responses = await Promise.all(schedules.map((schedule) => loadScheduleAvailability({
                practiceId, practitioner: selectedPractitioner, schedule, schedules,
                start: dateStr, end: dateStr, treatmentId, duration, leadId, timeZone,
            })));
            const unique = uniqueSlots(responses.flat());

            if (request === slotsRequest.current) {
                setSlots(unique);
                if (suggestionKey) {
                    const match = unique.find((candidate) => slotIdentity(candidate) === suggestionKey);
                    onSlotSelected(match || null);
                    setSuggestionMissing(!match);
                }
            }
        } catch (err) {
            if (request === slotsRequest.current) setSlotsError(err.message?.startsWith('Timezone-aware slots') ? err.message : 'Failed to load available slots. Please try again.');
        } finally {
            if (request === slotsRequest.current) setSlotsLoading(false);
        }
    }, [practiceId, selectedPractitioner?.id, selectedDate, schedules, duration, leadId, treatmentId, requireTreatment, suggestionKey, timeZone]);

    useEffect(() => {
        // Clear slot when date, practitioner, or duration changes
        onSlotSelected(null);
        fetchSlots();
        return () => { slotsRequest.current += 1; };
    }, [practiceId, selectedPractitioner?.id, selectedDate?.toDateString(), duration, schedules, leadId, treatmentId, requireTreatment, suggestionKey, timeZone]);

    useImperativeHandle(ref, () => ({
        submit() {
            const missing = [];
            if (requireTreatment && !treatmentId) missing.push('Treatment');
            if (!selectedPractitioner) missing.push('Practitioner');
            if (!selectedDate) missing.push('Date');
            if (!pmsSlot) missing.push('Appointment slot');
            if (missing.length) {
                enqueueSnackbar(`Please select: ${missing.join(', ')}`, {
                    variant: 'warning',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
                return;
            }
            onSubmit(pmsSlot);
        },
    }), [selectedPractitioner, selectedDate, pmsSlot, onSubmit, enqueueSnackbar, requireTreatment, treatmentId]);

    const { morning, afternoon, evening } = groupSlotsByPeriod(slots, timeZone);
    const hasSlots = slots.length > 0;
    const practiceToday = timeZone ? new Date(`${new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())}T12:00:00`) : new Date();
    const maxBookingDate = new Date(practiceToday);
    maxBookingDate.setDate(maxBookingDate.getDate() + 90);
    const scheduleZones = [...new Set(schedules.map((schedule) => schedule.timezone).filter(Boolean))];
    const showSlotArea = selectedPractitioner && selectedDate && (!requireTreatment || treatmentId);

    return (
        <Box>
            {/* Practitioner selector */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:account-circle-outline" width={15} sx={{ color: 'primary.main' }} />
                    Practitioner
                </Typography>

                {practitionersError && <Alert severity="error" sx={{ mb: 1.5 }} action={<Button onClick={() => setMetadataRetry((value) => value + 1)}>Retry practitioners</Button>}>{practitionersError}</Alert>}

                {practitionersLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading practitioners&hellip;</Typography>
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {practitioners.map((p) => {
                            const isSelected = selectedPractitioner?.id === p.id;
                            const displayName = p.name || p.full_name || `Practitioner ${p.id}`;
                            return (
                                <Button
                                    key={p.id}
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => onPractitionerSelected(isSelected ? null : p)}
                                    startIcon={<Iconify icon="mdi:account" width={14} />}
                                    sx={{
                                        borderRadius: '50px',
                                        textTransform: 'none',
                                        fontWeight: isSelected ? 700 : 400,
                                        fontSize: '0.8125rem',
                                        ...(isSelected
                                            ? {
                                                bgcolor: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                            }
                                            : {
                                                border: '1px solid rgba(145,158,171,0.32)',
                                                color: 'text.secondary',
                                                bgcolor: 'transparent',
                                                '&:hover': { bgcolor: 'rgba(145,158,171,0.08)', borderColor: 'rgba(145,158,171,0.48)' },
                                            }),
                                    }}
                                >
                                    {displayName}
                                </Button>
                            );
                        })}
                        {!practitionersLoading && !practitionersError && practitioners.length === 0 && (
                            <Typography variant="caption" color="text.disabled">
                                No practitioners configured for this practice.
                            </Typography>
                        )}
                    </Box>
                )}

                {schedulesLoading && selectedPractitioner && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <CircularProgress size={12} />
                        <Typography variant="caption" color="text.secondary">Loading schedule&hellip;</Typography>
                    </Stack>
                )}

                {schedulesError && <Alert severity="error" action={<Button onClick={() => setMetadataRetry((value) => value + 1)}>Retry schedules</Button>}>{schedulesError}</Alert>}
                {selectedPractitioner && !schedulesLoading && !schedulesError && schedules.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        No schedules found for this practitioner.
                    </Alert>
                )}
            </Box>

            {/* Date picker */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:calendar" width={15} sx={{ color: '#00AB55' }} />
                    Date
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        label="Select date"
                        value={selectedDate}
                        onChange={onDateSelected}
                        minDate={practiceToday}
                        maxDate={maxBookingDate}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                            },
                        }}
                    />
                </LocalizationProvider>
            </Box>

            {/* Native treatment duration is derived by the server. */}
            {treatmentId && <Alert severity="info" sx={{ mb: 2 }}>Appointment duration is determined by the selected treatment and patient visit history. Each slot shows its actual end time.</Alert>}
            {!treatmentId && !requireTreatment && <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon="mdi:clock-time-four-outline" width={15} sx={{ color: '#6366F1' }} />
                    Duration
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {[15, 20, 30, 45, 60].map((min) => (
                        <Button
                            key={min}
                            variant={duration === min ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => { setDuration(min); onDurationChange?.(min); onSlotSelected(null); }}
                            sx={{
                                borderRadius: '50px',
                                textTransform: 'none',
                                fontWeight: duration === min ? 700 : 400,
                                fontSize: '0.8125rem',
                                minWidth: 60,
                                ...(duration === min
                                    ? { bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }
                                    : { border: '1px solid rgba(145,158,171,0.32)', color: 'text.secondary', bgcolor: 'transparent', '&:hover': { bgcolor: 'rgba(145,158,171,0.08)' } }),
                            }}
                        >
                            {min} min
                        </Button>
                    ))}
                </Box>
            </Box>}

            {!!scheduleZones.length && <Typography variant="body2" sx={{ mb: 2 }}>Schedule times: {scheduleZones.join(', ')}</Typography>}
            {/* Available slots */}
            {showSlotArea && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon="mdi:clock-outline" width={15} sx={{ color: '#FFAB00' }} />
                        Available Slots
                        {pmsSlot && (
                            <Chip
                                icon={<Iconify icon="mdi:check" width={12} />}
                                label={formatSlotTime(pmsSlot.start_time, pmsSlot._timezone || timeZone)}
                                size="small"
                                color="success"
                                sx={{ ml: 'auto', fontWeight: 700, fontSize: '11px' }}
                            />
                        )}
                    </Typography>

                    {suggestionMissing && <Alert severity="warning" sx={{ mb: 1.5 }}>The suggested appointment is no longer available for this patient and treatment. Choose another available time.</Alert>}
                    {slotsError && <Alert severity="error" sx={{ mb: 1.5 }} action={<Button disabled={slotsLoading} onClick={() => { onSlotSelected(null); fetchSlots(); }}>Retry availability</Button>}>{slotsError}</Alert>}

                    {slotsLoading && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                            <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                            <Typography variant="body2" color="text.secondary">Checking availability&hellip;</Typography>
                        </Stack>
                    )}

                    {!slotsLoading && !slotsError && schedules.length > 0 && (
                        <>
                            {!hasSlots && (
                                <Box sx={{ textAlign: 'center', py: 4, px: 2, bgcolor: 'rgba(145,158,171,0.04)', borderRadius: 2, border: '1px dashed', borderColor: 'rgba(145,158,171,0.3)' }}>
                                    <Iconify icon="mdi:calendar-remove-outline" width={32} sx={{ color: 'text.disabled', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">No slots available on this date.</Typography>
                                    <Typography variant="caption" color="text.disabled">Try a different date or practitioner.</Typography>
                                </Box>
                            )}

                            <SlotPeriodSection
                                label="Morning"
                                icon="mdi:weather-sunrise"
                                iconColor="#FF9800"
                                slots={morning}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                                duration={duration}
                                timeZone={timeZone}
                            />
                            <SlotPeriodSection
                                label="Afternoon"
                                icon="mdi:weather-sunny"
                                iconColor="#F59E0B"
                                slots={afternoon}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                                duration={duration}
                                timeZone={timeZone}
                            />
                            <SlotPeriodSection
                                label="Evening"
                                icon="mdi:weather-sunset"
                                iconColor="#7C3AED"
                                slots={evening}
                                selectedSlot={pmsSlot}
                                onSelect={onSlotSelected}
                                duration={duration}
                                timeZone={timeZone}
                            />
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
});

export default PmsAvailabilityWizardStep;
